import inkImage from '@/assets/leaders-container-ink.jpg';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';

const cadence = [
  {
    n: '01',
    label: 'Cadence',
    body: 'Bi-weekly, private, confidential. Six to twelve months at the start, often longer.',
  },
  {
    n: '02',
    label: 'Form',
    body: "A sustained developmental relationship. Each session shaped by what's alive in your real work and life that week.",
  },
  {
    n: '03',
    label: 'Aim',
    body: 'The slow, durable expansion of how you see, how you choose, and who you are when no one is watching.',
  },
];

const LeadersContainer = () => {
  const heading = useScrollReveal<HTMLDivElement>();
  const image = useScrollReveal<HTMLDivElement>();
  const list = useScrollReveal<HTMLOListElement>();

  return (
    <section className="relative bg-foreground text-background py-32 sm:py-44 overflow-hidden">
      {/* Subtle warm vignette */}
      <div
        className="absolute inset-0 pointer-events-none opacity-50"
        style={{
          background:
            'radial-gradient(ellipse at 80% 20%, hsl(var(--primary) / 0.10) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />

      <div className="container-mobile relative z-10 max-w-7xl mx-auto">
        {/* Header — full-width, asymmetric */}
        <div
          ref={heading.ref}
          className={`max-w-3xl mb-20 transition-all duration-1000 ${
            heading.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-12 bg-primary" aria-hidden="true" />
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
              The Container
            </p>
          </div>
          <h2
            className="text-background text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
            style={{ textWrap: 'balance' }}
          >
            What the work <span className="text-primary italic">actually is.</span>
          </h2>
        </div>

        {/* Asymmetric editorial spread */}
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          {/* Painterly image — anchored left, asymmetric */}
          <div
            ref={image.ref}
            className={`md:col-span-5 md:sticky md:top-32 transition-all duration-1000 ${
              image.visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
            }`}
          >
            <div className="relative aspect-[4/5] overflow-hidden rounded-sm shadow-2xl">
              <img
                src={inkImage}
                alt=""
                width={1024}
                height={1280}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    'linear-gradient(to bottom, transparent 60%, hsl(var(--foreground) / 0.4) 100%)',
                }}
              />
            </div>
            <p className="font-display italic text-primary/80 text-lg mt-6 pl-1">
              Practices you return to.
            </p>
          </div>

          {/* Numbered cadence markers */}
          <ol
            ref={list.ref}
            className={`md:col-span-7 space-y-12 md:space-y-16 transition-all duration-1000 delay-200 ${
              list.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            {cadence.map((item) => (
              <li key={item.label} className="border-l-2 border-primary/40 hover:border-primary transition-colors duration-500 pl-6 sm:pl-8">
                <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs mb-4">
                  {item.label}
                </p>
                <p className="text-background/85 text-lg sm:text-xl leading-relaxed font-light">
                  {item.body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};

export default LeadersContainer;
